import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Calendar, Package, MessageSquare, Search, Send, Pencil, Palette, FileDown, Image as ImageIcon, Sparkles } from "lucide-react";
import { adminPing, adminInbox, adminUpdateStatus, adminSendDigest, adminUpdateNote } from "@/lib/api";
import { TestSweepDialog } from "@/components/admin/TestSweepDialog";
import { trackAdminLogin } from "@/lib/analytics";
import { useSEO } from "@/lib/useSEO";

export const KEY_STORAGE = "mfs.admin_key";

const TABS = [
  { id: "all", label: "All", icon: RefreshCw },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "samples", label: "Samples", icon: Package },
  { id: "contacts", label: "Enquiries", icon: MessageSquare },
  { id: "guides", label: "Guides", icon: FileDown },
  { id: "shares", label: "Shares", icon: ImageIcon },
];

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function Gate({ onAuthed }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      await adminPing(pw);
      sessionStorage.setItem(KEY_STORAGE, pw);
      trackAdminLogin();
      onAuthed(pw);
    } catch {
      toast.error("Incorrect key.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121315] grain flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md space-y-8" data-testid="admin-gate">
        <div className="flex flex-col items-start">
          <img
            src="/logo/logo-primary.png"
            alt="My Floor Studio"
            className="w-16 h-16 mb-8"
          />
          <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-4">Studio Inbox</p>
          <h1 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-4xl md:text-5xl leading-[0.98]">
            Private access
          </h1>
          <p className="text-[#D6D1CA]/55 mt-4 text-sm font-body">
            Enter the studio admin key to view bookings, sample requests and enquiries.
          </p>
        </div>
        <input
          data-testid="admin-key-input"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoFocus
          placeholder="Admin key"
          className="field-underline w-full"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-key-submit"
          className="btn-bronze rounded-full px-10 py-3 text-[11px] tracking-[0.22em] uppercase font-medium disabled:opacity-60"
        >
          {loading ? "Checking…" : "Enter Studio"}
        </button>
      </form>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="text-center py-24 border border-dashed border-[#8C7355]/25">
      <p className="font-serif-display italic text-[#C9A876]/70 text-lg">Nothing here yet</p>
      <p className="text-[#D6D1CA]/45 text-xs tracking-[0.25em] uppercase mt-3">{label}</p>
    </div>
  );
}

const STATUS_ORDER = ["new", "actioned", "dispatched"];
const STATUS_STYLE = {
  new: { border: "#C9A876", bg: "rgba(201,168,118,0.14)", text: "#C9A876" },
  actioned: { border: "#8AA79E", bg: "rgba(138,167,158,0.14)", text: "#8AA79E" },
  dispatched: { border: "#5B7C6E", bg: "rgba(91,124,110,0.20)", text: "#B9D0C6" },
};

function StatusChip({ kind, id, status, onChange }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.new;
  const cycle = () => {
    const idx = STATUS_ORDER.indexOf(status || "new");
    const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
    onChange(kind, id, next);
  };
  return (
    <button
      data-testid={`status-chip-${id}`}
      onClick={cycle}
      title="Click to advance status"
      className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] tracking-[0.25em] uppercase font-medium"
      style={{ borderColor: s.border, background: s.bg, color: s.text, transition: "all 0.35s ease" }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.text }} />
      {status || "new"}
    </button>
  );
}

function BookingCard({ b, onStatus }) {
  return (
    <article data-testid={`booking-${b.id}`} className="border border-[#8C7355]/18 bg-[#1a1b1e]/60 p-5 md:p-6">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">Appointment · {b.role}</p>
          <h3 className="font-serif-display text-[#F5F4F0] text-2xl font-light tracking-tight mt-1">{b.name}</h3>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusChip kind="bookings" id={b.id} status={b.status} onChange={onStatus} />
          <span className="text-[#D6D1CA]/40 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap">{fmt(b.created_at)}</span>
        </div>
      </header>
      <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
        <div><dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Email</dt><dd className="text-[#F5F4F0] break-all">{b.email}</dd></div>
        {b.phone && <div><dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Phone</dt><dd className="text-[#F5F4F0]">{b.phone}</dd></div>}
        {b.date && <div><dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Preferred</dt><dd className="text-[#F5F4F0]">{b.date}{b.time ? ` @ ${b.time}` : ""}</dd></div>}
        {!b.date && b.time && <div><dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Preferred Time</dt><dd className="text-[#F5F4F0]">{b.time}</dd></div>}
        {b.message && <div className="sm:col-span-2"><dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Message</dt><dd className="text-[#D6D1CA]/85 font-body mt-1">{b.message}</dd></div>}
      </dl>
    </article>
  );
}

function SampleCard({ s, onStatus }) {
  return (
    <article data-testid={`sample-${s.id}`} className="border border-[#8C7355]/18 bg-[#1a1b1e]/60 p-5 md:p-6">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">Sample Request · {s.role}</p>
          <h3 className="font-serif-display text-[#F5F4F0] text-2xl font-light tracking-tight mt-1">{s.name}</h3>
          {s.company && <p className="text-[#D6D1CA]/60 text-xs font-body mt-1">{s.company}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusChip kind="samples" id={s.id} status={s.status} onChange={onStatus} />
          <span className="text-[#D6D1CA]/40 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap">{fmt(s.created_at)}</span>
        </div>
      </header>
      <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
        <div><dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Email</dt><dd className="text-[#F5F4F0] break-all">{s.email}</dd></div>
        {s.address && <div><dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Ship to</dt><dd className="text-[#F5F4F0]">{s.address}</dd></div>}
        {s.materials?.length > 0 && (
          <div className="sm:col-span-2">
            <dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Elements</dt>
            <dd className="mt-2 flex flex-wrap gap-2">
              {s.materials.map((m) => (
                <span key={m} className="text-[#F5F4F0] text-[10px] tracking-[0.2em] uppercase border border-[#8C7355]/45 px-3 py-1 rounded-full bg-[#8C7355]/12">
                  {m}
                </span>
              ))}
            </dd>
          </div>
        )}
        {s.products?.length > 0 && (
          <div className="sm:col-span-2">
            <dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Products</dt>
            <dd className="mt-2 space-y-1">
              {s.products.map((p) => (
                <div key={p.id || p.name} className="text-[#F5F4F0] text-sm">
                  <span className="text-[#C9A876] text-[10px] tracking-[0.25em] uppercase mr-2">{p.discipline}</span>
                  {p.name}
                  {p.finish ? <span className="text-[#D6D1CA]/45"> · {p.finish}</span> : null}
                </div>
              ))}
            </dd>
          </div>
        )}
        {s.notes && <div className="sm:col-span-2"><dt className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase">Brief</dt><dd className="text-[#D6D1CA]/85 font-body mt-1">{s.notes}</dd></div>}
      </dl>
    </article>
  );
}

function ContactCard({ c, onStatus }) {
  return (
    <article data-testid={`contact-${c.id}`} className="border border-[#8C7355]/18 bg-[#1a1b1e]/60 p-5 md:p-6">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">Enquiry</p>
          <h3 className="font-serif-display text-[#F5F4F0] text-2xl font-light tracking-tight mt-1">{c.name}</h3>
          <a href={`mailto:${c.email}`} className="text-[#D6D1CA]/70 text-xs mt-1 link-underline break-all">{c.email}</a>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusChip kind="contacts" id={c.id} status={c.status} onChange={onStatus} />
          <span className="text-[#D6D1CA]/40 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap">{fmt(c.created_at)}</span>
        </div>
      </header>
      <p className="text-[#D6D1CA]/85 font-body text-sm whitespace-pre-line">{c.message}</p>
    </article>
  );
}

function ShareCard({ s, onStatus }) {
  return (
    <article data-testid={`share-${s.id}`} className="border border-[#8C7355]/18 bg-[#1a1b1e]/60 p-5 md:p-6">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">Visualiser Share · {s.room}</p>
          <h3 className="font-serif-display text-[#F5F4F0] text-2xl font-light tracking-tight mt-1">{s.name}</h3>
          <a href={`mailto:${s.email}`} className="text-[#D6D1CA]/70 text-xs mt-1 link-underline break-all">{s.email}</a>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusChip kind="shares" id={s.id} status={s.status} onChange={onStatus} />
          <span className="text-[#D6D1CA]/40 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap">{fmt(s.created_at)}</span>
        </div>
      </header>
      <p className="text-[#F5F4F0] font-body text-sm">{s.spec}</p>
      {s.image_url && (
        <a href={s.image_url} target="_blank" rel="noreferrer" className="block mt-4 border border-[#8C7355]/20">
          <img src={s.image_url} alt={s.spec} loading="lazy" className="w-full max-h-56 object-cover" />
        </a>
      )}
    </article>
  );
}

function GuideCard({ g, onStatus }) {
  return (
    <article data-testid={`guide-${g.id}`} className="border border-[#8C7355]/18 bg-[#1a1b1e]/60 p-5 md:p-6">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">Guide Download</p>
          <h3 className="font-serif-display text-[#F5F4F0] text-2xl font-light tracking-tight mt-1">{g.name}</h3>
          <a href={`mailto:${g.email}`} className="text-[#D6D1CA]/70 text-xs mt-1 link-underline break-all">{g.email}</a>
          {g.company && <p className="text-[#D6D1CA]/60 text-xs font-body mt-1">{g.company}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusChip kind="guides" id={g.id} status={g.status} onChange={onStatus} />
          <span className="text-[#D6D1CA]/40 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap">{fmt(g.created_at)}</span>
        </div>
      </header>
      <p className="text-[#F5F4F0] font-body text-sm">{g.guide_title}</p>
    </article>
  );
}

function InboxView({ adminKey, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digesting, setDigesting] = useState(false);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminInbox(adminKey);
      setData(d);
    } catch {
      toast.error("Session expired.");
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [adminKey, onLogout]);

  useEffect(() => { load(); }, [load]);

  const handleStatus = useCallback(async (kind, id, status) => {
    // optimistic update
    setData((prev) => {
      if (!prev) return prev;
      const key = kind;
      return { ...prev, [key]: prev[key].map((r) => (r.id === id ? { ...r, status } : r)) };
    });
    try {
      await adminUpdateStatus(adminKey, kind, id, status);
    } catch {
      toast.error("Couldn't update status.");
      load();
    }
  }, [adminKey, load]);

  const handleDigest = useCallback(async () => {
    setDigesting(true);
    try {
      const res = await adminSendDigest(adminKey);
      if (res.sent) {
        toast.success(`Digest sent · ${res.total} new item${res.total !== 1 ? "s" : ""}`, {
          description: "Delivered to the studio inbox.",
        });
      } else {
        toast.info("Nothing new to digest.", { description: "All records have been actioned or dispatched." });
      }
    } catch {
      toast.error("Couldn't send digest.");
    } finally {
      setDigesting(false);
    }
  }, [adminKey]);

  const filtered = useMemo(() => {
    if (!data) return { bookings: [], samples: [], contacts: [], guides: [], shares: [] };
    const s = q.trim().toLowerCase();
    const match = (obj) => {
      if (!s) return true;
      return JSON.stringify(obj).toLowerCase().includes(s);
    };
    return {
      bookings: data.bookings.filter(match),
      samples: data.samples.filter(match),
      contacts: data.contacts.filter(match),
      guides: (data.guides || []).filter(match),
      shares: (data.shares || []).filter(match),
    };
  }, [data, q]);

  const feed = useMemo(() => {
    if (!filtered) return [];
    if (tab === "bookings") return filtered.bookings.map((b) => ({ k: "b", d: b }));
    if (tab === "samples") return filtered.samples.map((s) => ({ k: "s", d: s }));
    if (tab === "contacts") return filtered.contacts.map((c) => ({ k: "c", d: c }));
    if (tab === "guides") return filtered.guides.map((g) => ({ k: "g", d: g }));
    if (tab === "shares") return filtered.shares.map((sh) => ({ k: "sh", d: sh }));
    // all — merged by created_at desc
    return [
      ...filtered.bookings.map((d) => ({ k: "b", d })),
      ...filtered.samples.map((d) => ({ k: "s", d })),
      ...filtered.contacts.map((d) => ({ k: "c", d })),
      ...filtered.guides.map((d) => ({ k: "g", d })),
      ...filtered.shares.map((d) => ({ k: "sh", d })),
    ].sort((a, b) => (a.d.created_at < b.d.created_at ? 1 : -1));
  }, [filtered, tab]);

  const counts = data?.counts || { bookings: 0, samples: 0, contacts: 0, guides: 0, shares: 0, total: 0 };

  return (
    <div className="min-h-screen bg-[#121315] grain">
      <header className="border-b border-[#8C7355]/18 bg-[#121315]/95 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo/logo-primary.png"
              alt="My Floor Studio"
              className="w-11 h-11 shrink-0"
            />
            <div>
              <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase">Studio Inbox</p>
              <h1 className="font-serif-display text-[#F5F4F0] text-xl font-light tracking-tight">My Floor Studio</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/brand"
              data-testid="admin-brand-link"
              className="inline-flex items-center gap-2 rounded-full border border-[#8C7355]/45 bg-transparent px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[#F5F4F0] hover:bg-[#8C7355]/12"
              style={{ transition: "background-color 0.3s ease" }}
            >
              <Palette size={13} strokeWidth={1.6} />
              Brand Kit
            </Link>
            <button
              data-testid="admin-clear-test-entries"
              onClick={() => setSweeping(true)}
              className="inline-flex items-center gap-2 rounded-full border border-[#8C7355]/45 bg-transparent px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[#F5F4F0] hover:bg-[#8C7355]/12"
              style={{ transition: "background-color 0.3s ease" }}
            >
              <Sparkles size={13} strokeWidth={1.6} />
              Clear Test Entries
            </button>
            <button
              data-testid="admin-send-digest"
              onClick={handleDigest}
              disabled={digesting}
              className="inline-flex items-center gap-2 rounded-full border border-[#8C7355]/45 bg-[#8C7355]/12 px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[#F5F4F0] hover:bg-[#8C7355]/25 disabled:opacity-50"
              style={{ transition: "background-color 0.3s ease" }}
            >
              <Send size={13} strokeWidth={1.6} />
              {digesting ? "Sending…" : "Send Digest"}
            </button>
            <button
              data-testid="admin-refresh"
              onClick={load}
              aria-label="Refresh"
              className="text-[#D6D1CA] hover:text-[#F5F4F0] p-2"
              style={{ transition: "color 0.3s ease" }}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-[#D6D1CA] hover:text-[#C9A876] text-[11px] tracking-[0.2em] uppercase"
              style={{ transition: "color 0.3s ease" }}
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-12 md:py-16">
        {/* Stat strip */}
        <section className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-12">
          {[
            { l: "Total", v: counts.total },
            { l: "Bookings", v: counts.bookings },
            { l: "Samples", v: counts.samples },
            { l: "Enquiries", v: counts.contacts },
            { l: "Guides", v: counts.guides || 0 },
            { l: "Shares", v: counts.shares || 0 },
          ].map((s) => (
            <div key={s.l} className="border border-[#8C7355]/18 bg-[#1a1b1e]/50 p-5 md:p-6">
              <p className="text-[#C9A876]/75 text-[10px] tracking-[0.3em] uppercase">{s.l}</p>
              <p className="font-serif-display text-[#F5F4F0] text-4xl md:text-5xl font-light mt-3">{s.v}</p>
            </div>
          ))}
        </section>

        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
            {TABS.map((t) => {
              const on = tab === t.id;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className="inline-flex items-center gap-2 rounded-full border px-5 py-2 text-[11px] tracking-[0.22em] uppercase"
                  style={{
                    borderColor: on ? "#8C7355" : "rgba(214,209,202,0.22)",
                    background: on ? "rgba(140,115,85,0.2)" : "transparent",
                    color: on ? "#F5F4F0" : "rgba(214,209,202,0.75)",
                    transition: "all 0.35s ease",
                  }}
                >
                  <Icon size={13} strokeWidth={1.6} />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="relative w-full md:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D6D1CA]/45" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, brief…"
              className="w-full bg-[#1a1b1e]/60 border border-[#8C7355]/20 pl-9 pr-4 py-2.5 text-sm text-[#F5F4F0] placeholder:text-[#D6D1CA]/35 focus:outline-none focus:border-[#8C7355]/60"
              style={{ transition: "border-color 0.3s ease" }}
            />
          </div>
        </div>

        {/* Feed */}
        <section className="space-y-4" data-testid="admin-feed">
          <AnimatePresence mode="popLayout">
            {feed.length === 0 && !loading && <EmptyState label={tab} />}
            {feed.map((item, i) => (
              <motion.div
                layout
                key={`${item.k}-${item.d.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, delay: (i % 12) * 0.02, ease: [0.22, 1, 0.36, 1] }}
              >
                {item.k === "b" && <BookingCard b={item.d} onStatus={handleStatus} />}
                {item.k === "s" && <SampleCard s={item.d} onStatus={handleStatus} />}
                {item.k === "c" && <ContactCard c={item.d} onStatus={handleStatus} />}
                {item.k === "g" && <GuideCard g={item.d} onStatus={handleStatus} />}
                {item.k === "sh" && <ShareCard s={item.d} onStatus={handleStatus} />}
              </motion.div>
            ))}
          </AnimatePresence>
        </section>
      </main>

      {sweeping && (
        <TestSweepDialog
          adminKey={adminKey}
          archivedCount={counts.archived || 0}
          onClose={() => setSweeping(false)}
          onDone={load}
        />
      )}
    </div>
  );
}

export default function Admin() {
  useSEO({ title: "Studio Inbox — My Floor Studio", description: "Private admin inbox.", path: "/admin", noindex: true });
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");

  useEffect(() => {
    if (!key) return;
    // validate the stored key on mount
    adminPing(key).catch(() => {
      sessionStorage.removeItem(KEY_STORAGE);
      setKey("");
    });
  }, [key]);

  const logout = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    setKey("");
  };

  return key ? <InboxView adminKey={key} onLogout={logout} /> : <Gate onAuthed={setKey} />;
}
